import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Info,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  X,
  LucideIcon,
} from "lucide-react";
import styles from "./Toast.module.css";

type Variant = "info" | "success" | "warn" | "danger";

interface Toast {
  id: string;
  variant: Variant;
  title?: string;
  message: string;
}

interface Ctx {
  push: (t: Omit<Toast, "id">) => void;
}

const ICONS: Record<Variant, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warn: AlertTriangle,
  danger: AlertCircle,
};

const ToastCtx = createContext<Ctx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((t: Omit<Toast, "id">) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { ...t, id }]);
  }, []);
  function dismiss(id: string) {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }
  return (
    <ToastCtx.Provider value={{ push }}>
      {children}
      <div className={styles.stack}>
        {toasts.map((t) => (
          <ToastItem key={t.id} t={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

function ToastItem({ t, onDismiss }: { t: Toast; onDismiss: () => void }) {
  useEffect(() => {
    const h = setTimeout(onDismiss, 5000);
    return () => clearTimeout(h);
  }, [onDismiss]);
  const Icon = ICONS[t.variant];
  return (
    <div className={`${styles.toast} ${styles[t.variant]}`} role="status">
      <span className={styles.icon} aria-hidden="true">
        <Icon size={18} />
      </span>
      <div className={styles.body}>
        {t.title && <div className={styles.title}>{t.title}</div>}
        <div className={styles.message}>{t.message}</div>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        className={styles.close}
        onClick={onDismiss}
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

export function useToast() {
  const c = useContext(ToastCtx);
  if (!c) throw new Error("useToast must be inside ToastProvider");
  return c;
}

/**
 * Like {@link useToast} but returns `null` when no provider is mounted instead
 * of throwing. Used by global UI (e.g. NetworkBanner) so it stays usable in
 * unit tests that don't wrap with ToastProvider.
 */
export function useOptionalToast(): Ctx | null {
  return useContext(ToastCtx);
}
